import {
  Animation,
  EventTracker,
  Palette,
  Stack,
} from '@devexpress/dx-react-chart';
import {
  ArgumentAxis,
  BarSeries,
  Chart,
  Legend,
  Tooltip as DxTooltip,
  ValueAxis,
} from '@devexpress/dx-react-chart-material-ui';
import { Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Info from '@material-ui/icons/InfoOutlined';
import { TooltipOverlay } from 'components';
import React from 'react';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { IChartValidityStatusProps } from './types';

import useStyles from './style';

const ChartStatusStock = ({ data, dataSeries }: IChartValidityStatusProps) => {
  const classes = useStyles();
  const { width } = useWindowDimensions();

  const mustAbbreviate = React.useMemo(() => {
    // largura aproximada de uma tela fullhd
    return width < 1900;
  }, [width]);

  const PointComponent = React.useCallback(
    ({ seriesIndex, index, ...props }) => {
      // seriesIndex === 1 valor da segunda pilha da coluna, as pilhas começam em 0
      const { total } = data[index];
      const clusterTotal = dataSeries.length - 1;
      let totalUnit;
      let abrevTotal;

      if (typeof total === 'number') {
        if (total >= 1000) {
          totalUnit = 'K';
        } else if (total >= 1000000) {
          totalUnit = 'M';
        }
      }

      // Total a ser mostrado no gráfico
      if (total >= 1000) {
        abrevTotal = `${String(total).substring(0, 1)}${totalUnit}`;
      }
      if (total >= 10000) {
        abrevTotal = `${String(total).substring(0, 2)}${totalUnit}`;
      }
      if (total >= 100000) {
        abrevTotal = `${String(total).substring(0, 3)}${totalUnit}`;
      }
      if (total >= 1000000) {
        abrevTotal = `${String(total).substring(0, 1)}${totalUnit}`;
      }
      if (total <= 999) {
        abrevTotal = String(total);
      }

      return (
        <>
          <BarSeries.Point {...props} />
          <Tooltip title={`${total}`} className={classes.labelItem}>
            <Chart.Label
              y={props.arg}
              // O gráfico provê uma função para retornar o ponto de X. Adicionando 8px para afasta a label da barra
              x={props.scales.valScale(total) + 8}
              className={classes.barLabel}
            >
              {seriesIndex === clusterTotal ? abrevTotal : ''}
            </Chart.Label>
          </Tooltip>
        </>
      );
    },
    [classes.barLabel, classes.labelItem, data, dataSeries.length],
  );

  const axiosLabelComponent = React.useCallback(
    (props: ArgumentAxis.LabelProps) => {
      let abrev = props.text;
      if (mustAbbreviate && typeof abrev === 'string' && abrev.length >= 9) {
        abrev = `${abrev.substring(0, 7)}...`;
      }
      return (
        <Tooltip title={props.text}>
          <ArgumentAxis.Label
            className={classes.labelItem}
            {...props}
            text={abrev}
          />
        </Tooltip>
      );
    },
    [classes.labelItem, mustAbbreviate],
  );

  const LabelMarker = React.useCallback(
    ({ color }) => {
      return (
        <>
          <rect
            style={{
              width: 8,
              height: 8,
              background: color,
            }}
          />
          <Legend.Marker className={classes.labelMarker} color={color} />
        </>
      );
    },
    [classes.labelMarker],
  );

  const LabelComponent = React.useCallback(
    ({ ...props }) => {
      return <Legend.Label {...props} className={classes.labelText} />;
    },
    [classes.labelText],
  );

  const LabelRoot = React.useCallback(
    ({ children, ...rest }) => {
      return (
        <Legend.Root {...rest} className={classes.labelContainer}>
          {children}
        </Legend.Root>
      );
    },
    [classes.labelContainer],
  );

  const TooltipContent = React.useCallback(
    ({ targetItem, ...props }) => {
      const series = data[targetItem.point];
      const serieName = targetItem.series;

      const seriesValue = data[targetItem.point][serieName];

      const { total } = series;
      const percent = (Number(seriesValue) / Number(total)) * 100;
      const textValue = `${seriesValue}; ${percent.toFixed(2)}%`;

      return (
        <DxTooltip.Content
          {...props}
          targetItem={targetItem}
          text={textValue}
        />
      );
    },
    [data],
  );

  if (!data || !data.length) {
    return (
      <div className={classes.noData}>
        <Info />
        <p>Sem dados disponíveis</p>
      </div>
    );
  }

  return (
    <Paper className={classes.statusChart}>
      <Chart rotated data={data} height={250}>
        <Palette scheme={['#FF7979', '#FFE34C', '#87E78D']} />
        <ArgumentAxis labelComponent={axiosLabelComponent} />
        <ValueAxis showGrid={false} showLabels={false} />

        {dataSeries.map((clusterName, idx) => {
          return (
            <BarSeries
              key={idx}
              name={clusterName}
              valueField={clusterName}
              argumentField="answer"
              pointComponent={PointComponent}
            />
          );
        })}

        <Animation />
        <Legend
          position="bottom"
          markerComponent={LabelMarker}
          labelComponent={LabelComponent}
          rootComponent={LabelRoot}
        />

        <Stack
          stacks={[
            {
              series: dataSeries,
            },
          ]}
        />
        <EventTracker />
        <DxTooltip
          overlayComponent={TooltipOverlay}
          contentComponent={TooltipContent}
        />
      </Chart>
    </Paper>
  );
};

export default ChartStatusStock;
